package com.evoapps.evoshift.ui.roster

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.unit.dp
import com.evoapps.evoshift.domain.usecase.roster.CopyOpenReason
import com.evoapps.evoshift.domain.usecase.roster.CopyPreview
import com.evoapps.evoshift.domain.usecase.roster.KeptShiftReason
import com.evoapps.evoshift.domain.usecase.roster.PlannedDaysMode
import androidx.compose.runtime.SideEffect
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import com.evoapps.evoshift.ui.LightGold
import com.evoapps.evoshift.widgets.EvoRadioGroup
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.copy_from_alongside
import evoshift.composeapp.generated.resources.copy_from_kept_clocked
import evoshift.composeapp.generated.resources.copy_from_kept_swap
import evoshift.composeapp.generated.resources.copy_from_replaced
import evoshift.composeapp.generated.resources.copy_planned_add
import evoshift.composeapp.generated.resources.copy_planned_replace
import evoshift.composeapp.generated.resources.copy_planned_skip
import evoshift.composeapp.generated.resources.copy_planned_title
import evoshift.composeapp.generated.resources.copy_from_drafts
import evoshift.composeapp.generated.resources.copy_from_labour
import evoshift.composeapp.generated.resources.copy_from_left_open
import evoshift.composeapp.generated.resources.copy_from_nothing
import evoshift.composeapp.generated.resources.copy_from_reason_absent
import evoshift.composeapp.generated.resources.copy_from_reason_double_booked
import evoshift.composeapp.generated.resources.copy_from_reason_inactive
import evoshift.composeapp.generated.resources.copy_from_reason_unavailable
import evoshift.composeapp.generated.resources.copy_from_skipped_closed
import evoshift.composeapp.generated.resources.copy_from_skipped_planned
import evoshift.composeapp.generated.resources.copy_from_total
import evoshift.composeapp.generated.resources.copy_from_unassigned_in_source
import kotlinx.datetime.LocalDate
import org.jetbrains.compose.resources.StringResource
import org.jetbrains.compose.resources.stringResource

/** SH-114: the text of a [CopyOpenReason] ("already working then: 2"). */
fun copyReasonRes(reason: CopyOpenReason): StringResource = when (reason) {
    CopyOpenReason.INACTIVE -> Res.string.copy_from_reason_inactive
    CopyOpenReason.ABSENT -> Res.string.copy_from_reason_absent
    CopyOpenReason.UNAVAILABLE -> Res.string.copy_from_reason_unavailable
    CopyOpenReason.DOUBLE_BOOKED -> Res.string.copy_from_reason_double_booked
}

/**
 * SH-199: Skip, Add or Replace for the target days that already have shifts, with how many. Only
 * when there are such days (known from the last preview, so the choice stays while a new one is
 * worked out).
 */
@Composable
internal fun PlannedDaysChoice(preview: CopyPreview?, mode: PlannedDaysMode, onMode: (PlannedDaysMode) -> Unit) {
    val known = remember { mutableStateOf<Map<LocalDate, Int>>(emptyMap()) }
    if (preview != null) SideEffect { known.value = preview.daysWithShifts }
    val days = preview?.daysWithShifts ?: known.value
    if (days.isEmpty()) return
    Text(
        stringResource(Res.string.copy_planned_title, days.size, days.values.sum()),
        color = LightGold,
        style = MaterialTheme.typography.bodyMedium
    )
    val labels = mapOf(
        PlannedDaysMode.SKIP to stringResource(Res.string.copy_planned_skip),
        PlannedDaysMode.ADD to stringResource(Res.string.copy_planned_add),
        PlannedDaysMode.REPLACE to stringResource(Res.string.copy_planned_replace)
    )
    EvoRadioGroup(
        options = PlannedDaysMode.entries,
        selected = mode,
        onSelect = onMode,
        optionLabel = { labels.getValue(it) }
    )
}

/** The preview's counts: what is copied, who was left out and why, what is skipped, what is flagged. */
@Composable
internal fun CopyPreviewCounts(preview: CopyPreview, nothing: String? = null) {
    Column(verticalArrangement = Arrangement.spacedBy(4.dp), modifier = Modifier.padding(top = 8.dp)) {
        val body = MaterialTheme.typography.bodyMedium
        val strong = Color.White
        val muted = Color.White.copy(alpha = 0.75f)
        if (preview.shifts.isEmpty()) {
            Text(nothing ?: stringResource(Res.string.copy_from_nothing), color = strong, style = body)
        } else {
            Text(stringResource(Res.string.copy_from_total, preview.shifts.size, preview.assigned), color = strong, style = MaterialTheme.typography.bodyLarge)
        }
        if (preview.unassignedInSource > 0) {
            Text(stringResource(Res.string.copy_from_unassigned_in_source, preview.unassignedInSource), color = muted, style = body)
        }
        if (preview.leftOpenTotal > 0) {
            Text(stringResource(Res.string.copy_from_left_open, preview.leftOpenTotal), color = LightGold, style = body)
            CopyOpenReason.entries.forEach { reason ->
                val count = preview.leftOpen[reason] ?: 0
                if (count > 0) Text("• ${stringResource(copyReasonRes(reason), count)}", color = muted, style = body, modifier = Modifier.padding(start = 12.dp))
            }
        }
        if (preview.labourWarnings > 0) {
            Text(stringResource(Res.string.copy_from_labour, preview.labourWarnings), color = LightGold, style = body)
        }
        if (preview.skippedClosed > 0) {
            Text(stringResource(Res.string.copy_from_skipped_closed, preview.skippedClosed), color = muted, style = body)
        }
        if (preview.skippedPlanned > 0) {
            Text(stringResource(Res.string.copy_from_skipped_planned, preview.skippedPlanned), color = muted, style = body)
        }
        // SH-199: what Replace removes and what stays, what Add keeps.
        if (preview.replaced.isNotEmpty()) {
            Text(stringResource(Res.string.copy_from_replaced, preview.replaced.size), color = LightGold, style = body)
        }
        preview.keptByReason[KeptShiftReason.CLOCKED]?.let {
            Text(stringResource(Res.string.copy_from_kept_clocked, it), color = muted, style = body)
        }
        preview.keptByReason[KeptShiftReason.IN_SWAP]?.let {
            Text(stringResource(Res.string.copy_from_kept_swap, it), color = muted, style = body)
        }
        if (preview.mode == PlannedDaysMode.ADD && preview.existingShifts > 0 && preview.shifts.isNotEmpty()) {
            Text(stringResource(Res.string.copy_from_alongside, preview.existingShifts), color = muted, style = body)
        }
        if (preview.shifts.isNotEmpty()) {
            Text(stringResource(Res.string.copy_from_drafts), color = Color.White.copy(alpha = 0.6f), style = MaterialTheme.typography.bodySmall)
        }
    }
}
